import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { Building2, FileText, Link2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { ListAiModels } from '@/domain/use-cases/ai-models.js';
import { ErrorState } from '@/presentation/components/index.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';
import type { SapConnectionController } from '@/presentation/controllers/sap-connection-controller.js';
import { useAiModels } from '@/presentation/hooks/use-ai-models.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useClientDocuments } from '@/presentation/hooks/use-client-documents.js';
import { useClientForm } from '@/presentation/hooks/use-client-form.js';
import { areThemeColorsValid } from '@/presentation/pages/clients/components/client-theme-section.js';
import { DeleteClientDocumentDialog } from '@/presentation/pages/clients/components/dialogs/delete-client-document-dialog.js';
import { UploadClientDocumentDialog } from '@/presentation/pages/clients/components/dialogs/upload-client-document-dialog.js';
import { ClientDadosTab } from '@/presentation/pages/clients/components/tabs/client-dados-tab.js';
import { ClientDocsTab } from '@/presentation/pages/clients/components/tabs/client-docs-tab.js';
import { ClientSapTab } from '@/presentation/pages/clients/components/tabs/client-sap-tab.js';
import { resolveClientPermissions } from '@/presentation/pages/clients/utils/resolve-client-permissions.js';
import { normalizeEmailDomains, validateClientForm } from '@/presentation/pages/clients/utils/validate-client-form.js';
import { primaryCtaGradient } from '@/shared/utils/brand-gradient.js';
import { fileToBase64 } from '@/shared/utils/file-to-base64.js';
import { formatDate } from '@/shared/utils/format-date.js';

type TabKey = 'dados' | 'sap' | 'docs';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    controller: ClientController;
    sapConnectionController: SapConnectionController;
    listAiModelsUseCase: ListAiModels;
    mode: 'create' | 'edit';
    clientId?: string;
}

export function ClientFormDialog({ open, onClose, onSaved, controller, sapConnectionController, listAiModelsUseCase, mode, clientId }: Props) {
    const { translate, useAuthStore, useThemeStore } = useApp();
    const { catalog: aiModelsCatalog, isLoading: aiModelsLoading } = useAiModels(listAiModelsUseCase);
    const currentUser = useAuthStore((s) => s.user);
    const applyThemePreset = useThemeStore((s) => s.applyPreset);
    const applyClientTheme = useThemeStore((s) => s.applyClientTheme);
    const resetToDefaultTheme = useThemeStore((s) => s.resetToDefaultTheme);
    const restoreSessionTheme = useThemeStore((s) => s.loadTheme);
    const sessionHeaderLogo = useThemeStore((s) => s.theme.logos.header);
    const isEdit = mode === 'edit';
    const { canEditClientData, canEditSapConnection } = resolveClientPermissions(currentUser);

    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<TabKey>('dados');
    const [docDeleteId, setDocDeleteId] = useState<string | null>(null);
    const [uploadModalCategory, setUploadModalCategory] = useState<string | null>(null);
    const [createdClientId, setCreatedClientId] = useState<string | null>(null);
    const [saveError, setSaveError] = useState('');

    // While creating a client, documents are staged locally (temp- ids) and only
    // persisted once the client itself exists — either right after creation succeeds, or on a
    // retry if a previous save only partially succeeded (client created, some sub-resource failed).
    const effectiveClientId = clientId ?? createdClientId ?? undefined;

    const { form, setField, logoUrl, logoFile, setLogoFile, createdAt, loading, loadError, logoError, uploadLogo, removeLogo } = useClientForm(
        controller,
        aiModelsCatalog,
        open,
        isEdit,
        clientId
    );
    const { documents, uploadingCategory, uploadDocument, downloadDocument, deleteDocument, persistPending } = useClientDocuments(
        controller,
        currentUser,
        effectiveClientId,
        open,
        isEdit,
        clientId
    );

    useEffect(() => {
        if (!open) {
            return;
        }
        setActiveTab('dados');
        setCreatedClientId(null);
        setSaveError('');
    }, [open]);

    useEffect(() => {
        if (!open || loading) {
            return;
        }
        if (form.themeEnabled && areThemeColorsValid(form.themeColors)) {
            applyThemePreset(form.themeColors);
        } else if (!form.themeEnabled) {
            restoreSessionTheme();
        }
    }, [open, loading, form.themeEnabled, form.themeColors, applyThemePreset, restoreSessionTheme]);

    const revertPreviewAndClose = () => {
        restoreSessionTheme();
        onClose();
    };

    const handleSave = async () => {
        const validationErrorKey = validateClientForm(form);
        if (validationErrorKey) {
            setActiveTab('dados');
            setSaveError(translate(validationErrorKey));
            return;
        }
        const normalizedDomains = normalizeEmailDomains(form.emailDomains);
        setSaveError('');
        setSaving(true);

        try {
            const slaConfig = {
                slaHoursEstimateEf: Number(form.slaHoursEstimateEf),
                slaHoursStartDev: Number(form.slaHoursStartDev),
                slaHoursAnalyzeBug: Number(form.slaHoursAnalyzeBug),
                slaHoursAnalyzeMinor: Number(form.slaHoursAnalyzeMinor),
                slaDaysValidateDoc: Number(form.slaDaysValidateDoc),
                slaMinScoreAi: Number(form.slaMinScoreAi),
                aiStrictnessLevel: Number(form.aiStrictnessLevel),
                aiExtraSystemPrompt: form.aiExtraSystemPrompt.trim()
            };

            const themePayload = {
                emailDomains: normalizedDomains,
                themeColors: form.themeEnabled ? form.themeColors : null
            };

            let targetClientId = effectiveClientId ?? null;

            if (isEdit) {
                const result = await controller.updateClient(clientId ?? '', {
                    name: form.name.trim(),
                    abbreviation: form.abbreviation.trim().toUpperCase(),
                    aiModel: form.aiModel,
                    ...slaConfig,
                    ...themePayload
                });
                if (result.isLeft()) {
                    setSaveError(result.value.message || translate('errors.generic'));
                    return;
                }
            } else if (!targetClientId) {
                const result = await controller.createClient({
                    name: form.name.trim(),
                    abbreviation: form.abbreviation.trim().toUpperCase(),
                    aiModel: form.aiModel,
                    ...slaConfig,
                    ...themePayload
                });
                if (result.isLeft()) {
                    setSaveError(result.value.message || translate('errors.generic'));
                    return;
                }
                targetClientId = result.value.id;
                setCreatedClientId(targetClientId);
            }

            if (logoFile && targetClientId) {
                const fileData = await fileToBase64(logoFile);
                const logoResult = await controller.uploadLogo(targetClientId, { fileData, fileName: logoFile.name, mimeType: logoFile.type });
                if (logoResult.isLeft()) {
                    setSaveError(logoResult.value.message || translate('errors.generic'));
                    return;
                }
                setLogoFile(null);
            }

            const failedCategories = targetClientId ? await persistPending(targetClientId) : [];

            if (failedCategories.length > 0) {
                const failureLabels = failedCategories.map((category) => `${translate('clients.tabs.docs')}: ${category}`);
                setSaveError(`${translate('clients.partialSaveError')}: ${failureLabels.join('; ')}`);
                return;
            }

            const currentUserDomain = currentUser?.email?.split('@')[1]?.toLowerCase() ?? '';
            const affectsCurrentUser = currentUserDomain.length > 0 && normalizedDomains.includes(currentUserDomain);
            if (affectsCurrentUser && form.themeEnabled) {
                applyClientTheme(form.themeColors, sessionHeaderLogo || null);
            } else if (affectsCurrentUser) {
                resetToDefaultTheme();
            } else {
                restoreSessionTheme();
            }

            onSaved();
            onClose();
        } catch {
            setSaveError(translate('errors.generic'));
        } finally {
            setSaving(false);
        }
    };

    const tabs = [
        { key: 'dados' as TabKey, label: translate('clients.tabs.dados'), icon: Building2 },
        { key: 'sap' as TabKey, label: translate('clients.tabs.sap'), icon: Link2 },
        { key: 'docs' as TabKey, label: translate('clients.tabs.docs'), icon: FileText }
    ];

    const gradientBtn = {
        textTransform: 'none' as const,
        background: primaryCtaGradient,
        color: 'white',
        '&:hover': { opacity: 0.9, background: primaryCtaGradient }
    };

    return (
        <Dialog open={open} onClose={revertPreviewAndClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                            {isEdit ? translate('clients.edit') : translate('clients.create')}
                        </Typography>
                        {isEdit && form.abbreviation && <Chip label={form.abbreviation} size="small" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', fontSize: 12 }} />}
                    </Stack>
                    {isEdit && createdAt && (
                        <Typography variant="caption" color="text.secondary">
                            {translate('clients.createdAtLabel')}: {formatDate(createdAt)}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={revertPreviewAndClose} size="small">
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            <Tabs value={activeTab} onChange={(_, v: TabKey) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return <Tab key={tab.key} value={tab.key} icon={<Icon size={16} />} iconPosition="start" label={tab.label} sx={{ textTransform: 'none', minHeight: 44 }} />;
                })}
            </Tabs>

            <DialogContent sx={{ p: 3 }}>
                {loading ? (
                    <Typography>{translate('common.loading')}</Typography>
                ) : loadError !== null ? (
                    <ErrorState message={loadError || undefined} />
                ) : (
                    <>
                        {logoError !== null && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {logoError || translate('errors.generic')}
                            </Alert>
                        )}
                        {activeTab === 'dados' && (
                            <ClientDadosTab
                                identity={{
                                    name: form.name,
                                    abbreviation: form.abbreviation,
                                    onNameChange: (value) => setField('name', value),
                                    onAbbreviationChange: (value) => setField('abbreviation', value),
                                    logoUrl,
                                    logoFile,
                                    isEdit,
                                    onLogoFileSelected: setLogoFile,
                                    onRemoveLogoFile: () => setLogoFile(null),
                                    onUploadLogoNow: uploadLogo,
                                    onRemoveLogo: removeLogo
                                }}
                                emailDomains={form.emailDomains}
                                onEmailDomainsChange={(domains) => setField('emailDomains', domains)}
                                theme={{
                                    enabled: form.themeEnabled,
                                    colors: form.themeColors,
                                    onEnabledChange: (enabled) => setField('themeEnabled', enabled),
                                    onColorsChange: (colors) => setField('themeColors', colors)
                                }}
                                sla={form}
                                onSlaFieldChange={setField}
                                aiModelsCatalog={aiModelsCatalog}
                                aiModelsLoading={aiModelsLoading}
                                readOnly={!canEditClientData}
                            />
                        )}

                        {activeTab === 'sap' && <ClientSapTab clientId={clientId} controller={sapConnectionController} canEdit={canEditSapConnection} />}

                        {activeTab === 'docs' && (
                            <ClientDocsTab
                                documents={documents}
                                uploadingCategory={uploadingCategory}
                                onUploadRequest={(category) => setUploadModalCategory(category)}
                                onDownload={async (doc) => {
                                    const ok = await downloadDocument(doc);
                                    if (!ok) {
                                        setSaveError(translate('clients.docDownloadError'));
                                    }
                                }}
                                onDeleteRequest={(docId) => setDocDeleteId(docId)}
                            />
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, alignItems: 'center' }}>
                {saveError && (
                    <Typography variant="body2" color="error" sx={{ mr: 'auto' }}>
                        {saveError}
                    </Typography>
                )}
                <Button onClick={revertPreviewAndClose} disabled={saving} sx={{ textTransform: 'none' }}>
                    {translate('common.cancel')}
                </Button>
                {canEditClientData && (
                    <Button onClick={handleSave} variant="contained" disabled={saving || loading} disableElevation sx={gradientBtn}>
                        {saving ? translate('devForm.saving') : translate('clients.saveChanges')}
                    </Button>
                )}
            </DialogActions>

            <DeleteClientDocumentDialog
                open={!!docDeleteId}
                onClose={() => setDocDeleteId(null)}
                onConfirm={() => (docDeleteId ? deleteDocument(docDeleteId) : Promise.resolve(false))}
            />

            <UploadClientDocumentDialog
                open={!!uploadModalCategory}
                category={uploadModalCategory}
                uploading={uploadingCategory === uploadModalCategory}
                onFileSelected={(file) => (uploadModalCategory ? uploadDocument(uploadModalCategory, file) : Promise.resolve(false))}
                onClose={() => setUploadModalCategory(null)}
            />
        </Dialog>
    );
}
